/**
 * Site settings & feature switches — frontend helpers.
 *
 * Reads GET /api/public/site-settings (public keys only) and caches the result
 * for up to 60 seconds, matching the backend cache window. isFeatureOn(name)
 * returns true only when `features.<name>` is boolean true; unknown/missing
 * switches are OFF.
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { API } from "@/App";

const TTL_MS = 60 * 1000;
let _cache = null;
let _cacheTs = 0;

export async function getPublicSettings({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && now - _cacheTs < TTL_MS) return _cache;
  try {
    const { data } = await axios.get(`${API}/public/site-settings`);
    _cache = data || {};
    _cacheTs = now;
  } catch {
    _cache = _cache || {};
  }
  return _cache;
}

export async function isFeatureOn(name) {
  const settings = await getPublicSettings();
  return settings?.[`features.${name}`] === true;
}

/** Synchronous check against the last-fetched settings (no network). */
export function isFeatureOnCached(name) {
  return _cache?.[`features.${name}`] === true;
}

/** React hook: returns the public settings map (empty object until loaded). */
export function usePublicSettings() {
  const [settings, setSettings] = useState(_cache || {});
  useEffect(() => {
    let mounted = true;
    getPublicSettings().then((s) => { if (mounted) setSettings(s || {}); });
    return () => { mounted = false; };
  }, []);
  return settings;
}
